import {useMediaQuery} from '@mui/material'
import Tab from '@mui/material/Tab'
import moment from 'moment'
import {FC, MouseEvent, MutableRefObject, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router'
import {useNavigate} from 'react-router-dom'
import {GetLikePhotos, LikePhoto, UnLikePhoto} from 'entities/activity'
import {
  PhotoType,
  PhotoTypes,
  useChatCanViewPrivatePhotos,
} from 'entities/messages'
import {
  changeFilterProfileMedia,
  clearStateProfileMedia,
  GalleryTypes,
  GetProfileMedia,
  openGallery,
  ProfileMediaTabs,
  setCurrentImage,
  setGalleryPhotos,
  setGalleryType,
  setLikePhoto,
  setLikePhotos,
  useGallery,
  useProfile,
  useProfileMedia,
  useProfileMediaCursor,
  useProfileMediaLikePhotos
} from 'entities/profile'
import {useUser} from 'entities/user'
import {Gallery} from 'features/gallery'
import {useVirtualScroll} from 'shared/hooks'
import {mediaVariants, skipFalsy} from 'shared/lib'
import {ActiveLike, Like, Lock, PaperPlane} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import {Tabs} from 'shared/ui/tabs'
import styles from './styles.module.scss'

type ProfileMediaProps = {
  wrapperRef: MutableRefObject<HTMLDivElement | null> | HTMLDivElement | null
}

export const ProfileMedia: FC<ProfileMediaProps> = ({wrapperRef}) => {
  const {id} = useParams()
  const dispatch = useDispatch()
  const cursor = useProfileMediaCursor()
  const media = useProfileMedia()
  const likePhotos = useProfileMediaLikePhotos()
  const {refetch: load, isFetching, isError} = GetProfileMedia(id || '', dispatch)
  const [activeTab, setActiveTab] = useState<ProfileMediaTabs>('all')
  const {galleryPhotos} = useGallery()
  const profile = useProfile()
  const {avatarUrl, videoAvatarUrl} = profile
  const is500p = useMediaQuery(`(max-width: ${styles.is500p})`)

  useVirtualScroll({ref: wrapperRef, isError, isFetching, load, cursor})

  useEffect(() => {
    setActiveTab('all')
    dispatch(clearStateProfileMedia())
  }, [id])

  useEffect(() => {
    const photos = [...(currentMedia || [])]

    if (is500p) {
      if (videoAvatarUrl) {
        photos.unshift({
          id: 'videoAvatar',
          type: PhotoTypes.VIDEO_AVATAR,
          image: {
            url: videoAvatarUrl,
            key: 'videoAvatar',
          }
        })
      } else if (avatarUrl) {
        photos.unshift({
          id: 'avatar',
          type: PhotoTypes.avatar,
          image: {
            url: avatarUrl,
            key: 'avatar',
          }
        })
      }
    }

    dispatch(setGalleryPhotos({photos, cursor}))
  }, [activeTab, media, avatarUrl, is500p])

  useEffect(() => {
    if (!media.all.length || !id) return

    const ids = media.all.map((item) => item.id).filter((item) => likePhotos[item] == null)
    if (!ids.length) return

    GetLikePhotos(id, ids)
      .then(({data}) => dispatch(setLikePhotos(data)))
  }, [media])

  const currentMedia = useMemo(() => {
    if(activeTab === 'all') return media.all
    if(activeTab === 'public') return media.public
    if(activeTab === 'private') return media.private
  }, [activeTab, media])

  return (
    <div className={styles.root}>
      <Tabs
        value={activeTab}
        onChange={(_, newValue: ProfileMediaTabs) => {
          dispatch(changeFilterProfileMedia(newValue))
          setActiveTab(newValue)
        }}
        className={styles.tabs}
        classes={{flexContainer: styles.tabsFlexContainer}}
      >
        <Tab
          value='all'
          label={<div className={styles.tabText}>All media</div>}
          className={styles.tab}
        />
        <Tab
          value='public'
          label={<div className={styles.tabText}>Public</div>}
          className={styles.tab}
        />
        <Tab
          value='private'
          label={<div className={styles.tabText}>Private</div>}
          className={styles.tab}
        />
      </Tabs>
      <div className={styles.profileMedia}>
        {currentMedia?.map((photo, index) => (
          <Media
            key={index}
            index={
              galleryPhotos.findIndex((item) => item.id === photo.id)
            }
            photo={photo}
            idUser={id || ''}
          />
        ))}
        {isFetching ? <Loader className={styles.profileMediaLoader} />  : null}
      </div>
      <Gallery load={load} isFetching={isFetching} isError={isError} />
    </div>
  )
}

type MediaProps = {
  photo: PhotoType
  idUser: string
  index: number
}

const Media: FC<MediaProps> = ({photo, idUser, index}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const likePhotos = useProfileMediaLikePhotos()
  const profile = useProfile()
  const [isAnimation, setIsAnimation] = useState(false)
  const is1024p = useMediaQuery(`(max-width: ${styles.is1024p})`)
  const {galleryPhotos} = useGallery()
  const isCanViewPrivatePhotos = useChatCanViewPrivatePhotos()
  const user = useUser()
  const {id} = useParams()
  const isClient = !!id
  const {name} = isClient ? profile : user.profile

  const openGalleryHandler = (type: PhotoTypes, index: number) => {
    const age = isClient ? profile.age : moment().diff(user.profile.dateBirth, 'years')

    dispatch(openGallery())
    dispatch(setGalleryType(GalleryTypes.profile))
    dispatch(setCurrentImage({
      index,
      type,
      id: galleryPhotos[index].id,
      src: galleryPhotos[index].image.url,
      label: `${skipFalsy(name, String(age)).join(', ')}`
    }))
  }

  const likeHandler = (e: MouseEvent) => {
    e.stopPropagation()

    if (likePhotos[photo.id]) {
      UnLikePhoto(idUser, photo.id)
        .then(() => dispatch(setLikePhoto({idPhoto: photo.id, value: false})))
    } else {
      setIsAnimation(true)
      LikePhoto(idUser, photo.id)
        .then(() => dispatch(setLikePhoto({idPhoto: photo.id, value: true})))
        .finally(() => setIsAnimation(false))
    }
  }

  if (photo.type === PhotoTypes.private && !isCanViewPrivatePhotos) {
    return (
      <div className={styles.media} onClick={() => openGalleryHandler(photo.type, index)}>
        <div className={`${styles.back} ${styles.private}`} style={{backgroundImage: `url(${photo.image.url + mediaVariants.square256})`}} />
        <div className={styles.privateWrapper}>
          <div className={styles.lock}><Lock /></div>
          <div className={styles.header}>Private media</div>
          {
            is1024p
              ? <div className={styles.text}>Chat to open private</div>
              : <>
                <div className={styles.text}>Start communication with {profile.name}. You'll get access after answer</div>
                <div
                  className={styles.chatNow}
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/messages/${profile.id}`)}
                  }
                >
                  <span>Chat  now</span>
                  <div className={styles.paperPlane}><PaperPlane /></div>
                </div>
              </>
          }
        </div>
      </div>
    )
  }
  const onClickMedia = () => {
    openGalleryHandler(photo.type, index)
  }

  return (
    <div className={styles.media} onClick={onClickMedia}>
      <div className={styles.back} style={{backgroundImage: `url(${photo.image.url + mediaVariants.square256})`}} />
      <img className={styles.content} src={photo.image.url + mediaVariants.originalS} alt='photo' />
      <div className={styles.likeWrapper} onClick={likeHandler}>
        <div
          className={`${styles.like} ${isAnimation ? styles.animate : ''}`}>
          {likePhotos[photo.id] || isAnimation ? <ActiveLike /> : <Like />}
        </div>
      </div>
    </div>
  )
}